// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { MIDDLEWARE_SLUG, endPoints } from '../../apis/apiEndPoints';
import { CampaignPostDataType, IResetDetailsPayload, JDAiGenerate, UserDataType, WithAiDataType, WithoutAiDataType } from '../../types/dataTypes';
import { baseQueryWithAuthCheck } from '../../utils/baseQueryWithAuthCheck';
import { extractBaseServer, extractDomain } from '../../utils/helpers';
import { TProvider } from '../../features/configureMail/ConfigureMail';


const DOMAIN = extractDomain();
const FETCH_CAMPAIGN_LIST = 'FETCH_CAMPAIGN_LIST';

export const folderNameApi = createApi({
    reducerPath: 'folderNameApi',
    baseQuery: baseQueryWithAuthCheck(fetchBaseQuery({
        baseUrl: `${window.location.origin}${MIDDLEWARE_SLUG}/middleware`
    })),
    tagTypes: [FETCH_CAMPAIGN_LIST],
    endpoints: (builder) => ({
        getFolderNameList: builder.mutation<any, any>({
            query: (folderName: string) => ({
                url: `${endPoints.FOLDER_LIST}`,
                method: 'POST',
                headers: {
                    'domain': DOMAIN,
                },
                withCredentials: true,
                body: {
                    query: folderName || ""
                }
            }),
        }),
        getAllFolders: builder.query<any, void>({
            query: () => ({
                url: endPoints.FOLDER_LIST,
                method: 'POST',
                headers: {
                    'domain': DOMAIN,
                },
                withCredentials: true,
                body: {
                    query: ""
                }
            }),
        }),
        postFolderData: builder.mutation({
            query: ({ folderName, userData }: { folderName: string; userData: UserDataType }) => ({
                url: `${endPoints.PERSONAL_FOLDER}`,
                method: 'POST',
                body: {
                    "channelId": userData.channelId,
                    "corpId": userData.corpId,
                    "folderName": folderName,
                    "subChannelId": userData.subChannelId,
                    "subUid": userData.recruiterId
                },
                headers: {
                    'domain': DOMAIN,
                },
                withCredentials: true,
            })
        }),
        getHeaderData: builder.query({
            query: (userData: UserDataType) => ({
                url: `${endPoints.HEADER}`,
                method: 'GET',
                headers: {
                    'domain': DOMAIN,
                },
                params: {
                    domain: DOMAIN,
                    platform: 'RECRUITER',
                    type: 'RECRUITER',
                    foundItUserId: userData.recruiterId,
                    channelId: userData.channelId,
                    subChannelId: userData.subChannelId,
                },
                withCredentials: true,
            }),
        }),
        postCampaignData: builder.mutation({
            query: (payload: CampaignPostDataType) => {
                return {
                    url: `${endPoints.CAMPAIGN_REQUEST}/${payload.status}/request`,
                    method: 'POST',
                    body: payload, // Use the FormData object as the body
                    headers: {
                         'domain': DOMAIN,
                    },
                    withCredentials: true,
                };
            },
        }),

        getAuthenticatedUser: builder.query({
            query: () => ({
                url: `${endPoints.AUTHENTICATE_USER}`,
                method: 'GET',
                headers: {
                    'domain': DOMAIN,
                    'baseserver': extractBaseServer(window.location.host)
                },
                params: {
                    domain: DOMAIN
                },
                withCredentials: true,
            }),
        }),
        getCampaignTemplates: builder.query({
            query: () => ({
                url: `${endPoints.CAMPAIGN_TEMPLATES}`,
                method: 'GET',
                headers: {
                    'domain': DOMAIN,
                },
                withCredentials: true,
            }),
        }),
        getEmailVerified: builder.query({
            query: () => ({
                url: `${endPoints.AUTHENTICATE_EMAIL}`,
                method: 'GET',
                headers: {
                    'domain': DOMAIN,
                },
                params: {
                    page: 'CAMPAIGN_CREATION'
                },
                withCredentials: true,
            })
        }),
        getAuthenticateEmail: builder.mutation({
            query: (payload?: {page?: string, provider?: TProvider}) => ({
                url: `${endPoints.AUTHENTICATE_EMAIL}`,
                method: 'GET',
                headers: {
                    'domain': DOMAIN,
                },
                params: {
                    page: payload?.page || '',
                    provider: payload?.provider || null
                },
                withCredentials: true,
            })
        }),
        getCampaignList: builder.query({
            query: (payload) => ({
                url: `${endPoints.CAMPAIGN_LIST}?page=${payload.pageNo}`,
                method: 'POST',
                headers: {
                    'domain': DOMAIN,
                },
                body: {
                    ...payload.filters
                },
                withCredentials: true,
            }),
            providesTags: [FETCH_CAMPAIGN_LIST],
        }),
        getCampaignStageDetails: builder.query({
            query: ({campaignId, isWhatsAppStage}:{campaignId: string, isWhatsAppStage:boolean}) => ({
                url: isWhatsAppStage ? `${endPoints.WHATS_APP_CAMPAIGN_STAGE}` : `${endPoints.CAMPAIGN_STAGE}`,
                method: 'GET',
                headers: {
                    'domain': DOMAIN,
                },
                params: {
                    campaignId: campaignId
                },
                withCredentials: true,
            }),
        }),
        getAlreadyContactedMailCount: builder.query({
            query: (payload: { campaignId: string, folderId: string }) => ({
                url: `${endPoints.CONTACTED_EMAILS_COUNT}/${payload.campaignId}/${payload.folderId}`,
                method: 'GET',
                headers: {
                    'domain': DOMAIN,
                },
                params: payload,
                withCredentials: true,
            }),
        }),
        getContactableMailCount: builder.query({
            query: (campaignId: string) => ({
                url: `${endPoints.CONTACTABLE_EMAILS_COUNT}/${campaignId}`,
                method: 'GET',
                headers: {
                    'domain': DOMAIN,
                },
                withCredentials: true,
            }),
        }),
        getPauseCampaign: builder.mutation({
            query: (campaignId: string) => ({
                url: `${endPoints.PAUSE_CAMPAIGN}/${campaignId}`,
                method: 'GET',
                headers: {
                    'domain': DOMAIN,
                },
                withCredentials: true,
            }),
            // refetch only on successfull api call
            invalidatesTags: (result: any, error: any) => (error ? [] : [FETCH_CAMPAIGN_LIST]),
        }),
        getResumeCampaign: builder.mutation({
            query: (campaignId: string) => ({
                url: `${endPoints.RESUME_CAMPAIGN}/${campaignId}`,
                method: 'GET',
                headers: {
                    'domain': DOMAIN,
                },
                withCredentials: true,
            }),
            // refetch only on successfull api call
            invalidatesTags: (result: any, error: any) => (error ? [] : [FETCH_CAMPAIGN_LIST]),
        }),
        getAbortCampaign: builder.mutation({
            query: (campaignId: string) => ({
                url: `${endPoints.ABORT_CAMPAIGN}/${campaignId}`,
                method: 'GET',
                headers: {
                    'domain': DOMAIN,
                },
                withCredentials: true,
            }),
            // refetch only on successfull api call
            invalidatesTags: (result: any, error: any) => (error ? [] : [FETCH_CAMPAIGN_LIST]),
        }),
        getCompanyDetails: builder.query({
            query: () => ({
                url: endPoints.COMPANY_DETAILS,
                method: 'GET',
                headers: {
                    'domain': DOMAIN,
                },
                withCredentials: true,
            }),
        }),
        postAiEmail: builder.mutation({
            query: (payload: WithAiDataType) => ({
                url: endPoints.WITH_AI,
                method: 'POST',
                body: payload,
                headers: {
                     'domain': DOMAIN,
                },
                withCredentials: true,
            })
        }),
        postRephraseEmail: builder.mutation({
            query: ({ payload, domain }: { payload: WithoutAiDataType; domain: string }) => ({
                url: endPoints.WITHOUT_AI,
                method: 'POST',
                body: payload,
                headers: {
                     'domain': domain ?? DOMAIN,
                },
                withCredentials: true,
            })
        }),

        // usePostResetTrackingMutation
        postResetTracking: builder.mutation({
            query: (payload: IResetDetailsPayload) => ({
                url: endPoints.RESET_TRACKING,
                method: 'POST',
                body: payload,
                headers: {
                     'domain': DOMAIN,
                },
                withCredentials: true,
            })
        }),
        getCompanyMedia: builder.query({
            query: () => ({
                url: endPoints.COMPANY_MEDIA,
                method: 'GET',
                headers: {
                    'domain': DOMAIN,
                },
                withCredentials: true,
            }),
        }),
        getSignature: builder.query({
            query: () => ({
                url: endPoints.SIGNATURE,
                method: 'GET',
                headers: {
                    'domain': DOMAIN,
                },
                withCredentials: true,
            }),
        }),
        getCampaignStatusList: builder.query({
            query: (pageNo) => ({
                url: endPoints.CAMPAIGN_STATUS_LIST,
                params: {
                    page: pageNo
                },
                withCredentials: true,
            }),
        }),
        getFAQS: builder.query({
            query: () => ({
                url: endPoints.FAQS,
                method: 'GET',
                headers: {
                    'domain': DOMAIN,
                },
                withCredentials: true,
            })
        }),
        getSeekersPending: builder.query({
            query: () => ({
                url: endPoints.SEEKERS_PENDING,
                method: 'GET',
                headers: {
                    'domain': DOMAIN,
                },
                withCredentials: true,
            }),
        }),
        trackHealth : builder.query<any, void>({
            query: (body) => ({
              url: endPoints.HEALTHTRACKER,
              method: "POST",
              headers: {
                'domain': DOMAIN,
            },
              withCredentials: true,  
              body: body,
            }),
          }),
          getInventoryPurchased: builder.query({
            query: (corpId) => ({
                url: `${endPoints.INVENTORY_PURCHASED}?corpId=${corpId}&type=OS`,
                method: 'GET',
                headers: {
                    'domain': DOMAIN,
                },
                withCredentials: true,
            }),
        }),
          getDraftData: builder.query({
            query: (campaignId) => ({
                url: endPoints.DRAFT_DATA + '?campaignId=' + campaignId,
                method: 'GET',
                headers: {
                    'domain': DOMAIN,
                },
                withCredentials: true,
            }),
        }),
        getSearchData : builder.query({
          query: (initiationId) => ({
            url: endPoints.SEARCH_ATTRIBUTES + '?id=' + initiationId,
            method: "GET",
            headers: {
              'domain': DOMAIN,
          },
            withCredentials: true,  
          }),
        }),
        getJobTitleList: builder.mutation<any, any>({
          query: ({jobTitle, random}:{jobTitle: string, random: number}) => ({
            //   url: `${endPoints.JOB_LIST}/${jobTitle || ''}?type=skill`,
              url: `${endPoints.JOB_LIST_V2}?q=${jobTitle || ""}&random=${random}&type=kwd`,
              method: 'GET',
              headers: {
                  'domain': DOMAIN,
              },
              withCredentials: true,
          }),
          transformResponse: (data: any) =>{
            const dataArr: string[] = data.split("|");
            return dataArr?.map(item => {
              const jobTitle = item.split("~")[0];
              return {label: jobTitle, value: jobTitle}}
            ) || []
          }
      }),
      generateJDByAi: builder.mutation({
        query: (payload: JDAiGenerate) => ({
            url: endPoints.JOB_DESCRIPTION_AI,
                method: 'POST',
                body: payload,
                headers: {
                     'domain': DOMAIN,
                },
                withCredentials: true,
        })
      })
    }),
});

export const {
    useGetFolderNameListMutation,
    useLazyGetAllFoldersQuery,
    usePostFolderDataMutation,
    useLazyGetHeaderDataQuery,
    usePostCampaignDataMutation,
    useGetAuthenticatedUserQuery,
    useGetCampaignTemplatesQuery,
    useGetEmailVerifiedQuery,
    useGetCampaignListQuery,
    useGetCampaignStageDetailsQuery,
    useLazyGetAlreadyContactedMailCountQuery,
    useGetContactableMailCountQuery,
    useGetPauseCampaignMutation,
    useGetCompanyDetailsQuery,
    useGetCompanyMediaQuery,
    usePostAiEmailMutation,
    usePostRephraseEmailMutation,
    useGetSignatureQuery,
    useGetCampaignStatusListQuery,
    useGetFAQSQuery,
    useGetSeekersPendingQuery,
    useGetResumeCampaignMutation,
    useGetAbortCampaignMutation,
    useLazyTrackHealthQuery,
    useGetInventoryPurchasedQuery,
    useGetDraftDataQuery,
    useGetSearchDataQuery,
    useGetJobTitleListMutation,
    useGenerateJDByAiMutation,
    usePostResetTrackingMutation,
    useGetAuthenticateEmailMutation
} = folderNameApi;

